import { useCallback, useState } from 'react';
import { Stack, Box, Typography } from '@mui/material';
import { StrategyCompass } from './StrategyCompass';
import { StrategyCompassEditSymbols } from './StrategyCompassEditSymbols';
import { StockScanner } from '../../../pages';
import { CompassProvider } from './CompassProvider';
import { useCompassConfig } from '../../../hooks/compass/useCompassConfig';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { CompassConfig } from '../../../types/compass';

export const StrategyCompassWithLayout = () => {
  const [config, updateConfig] = useCompassConfig();
  const [chartFullscreen, setChartFullscreen] = useState<boolean>(false);
  const [stockDetailsFullscreen, setStockDetailsFullscreen] =
    useState<boolean>(false);

  // Persist compass config to localStorage so it survives navigations.
  // On page load, if URL has no cmp_* params, previous settings are restored.
  const restoreConfig = useCallback(
    (saved: CompassConfig) => {
      updateConfig(saved);
    },
    [updateConfig],
  );

  usePersistedState('compass_query_state', config, restoreConfig, ['cmp']);

  // If in fullscreen mode, only show the compass chart
  if (chartFullscreen) {
    return (
      <CompassProvider config={config} onConfigChange={updateConfig}>
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            bgcolor: 'background.default',
            zIndex: 9_999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              width: '95%',
              height: '95%',
              bgcolor: 'background.paper',
              borderRadius: 2,
              p: 1,
            }}
          >
            <StrategyCompass
              chartFullscreen={chartFullscreen}
              setChartFullscreen={setChartFullscreen}
            />
          </Box>
        </Box>
      </CompassProvider>
    );
  }

  // If stock details is in fullscreen mode, only show the stock scanner
  if (stockDetailsFullscreen) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          bgcolor: 'background.default',
          zIndex: 9_999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            width: '95%',
            height: '95%',
            bgcolor: 'background.paper',
            borderRadius: 2,
            p: 1,
          }}
        >
          <StockScanner
            stockDetailsFullscreen={stockDetailsFullscreen}
            setStockDetailsFullscreen={setStockDetailsFullscreen}
          />
        </Box>
      </Box>
    );
  }

  return (
    <CompassProvider config={config} onConfigChange={updateConfig}>
      <Stack sx={{ width: '100%', gap: 3 }}>
        <Stack
          sx={{
            width: '100%',
            flexDirection: {
              xs: 'column',
              lg: 'row',
            },
            gap: 3,
          }}
        >
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 2,
              p: 1,
              flex: {
                xs: 1,
                lg: 2,
              },
            }}
          >
            <StrategyCompass
              chartFullscreen={chartFullscreen}
              setChartFullscreen={setChartFullscreen}
            />
          </Box>
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 2,
              p: 1,
              flex: 1,
            }}
          >
            <StrategyCompassEditSymbols />
          </Box>
        </Stack>
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 2,
            p: 1,
          }}
        >
          <Box
            sx={{
              pl: '12px',
              pt: '8px',
            }}
          >
            <Typography
              sx={{
                fontSize: {
                  xs: 14,
                  md: 16,
                },
                fontWeight: 'bold',
              }}
            >
              Stock Details
            </Typography>
          </Box>
          <StockScanner
            stockDetailsFullscreen={stockDetailsFullscreen}
            setStockDetailsFullscreen={setStockDetailsFullscreen}
          />
        </Box>
      </Stack>
    </CompassProvider>
  );
};
